import {
  Box,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Suspense, lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationProfileApi } from 'src/api/organization-profile';
import { treatmentApi } from 'src/api/treatment';
import { visitScopedPrescribingApi } from 'src/api/visit-scoped-prescribing';
import VetsiIcon from 'src/assets/icons/VetsiIcon';
import Iconify from 'src/components/iconify';
import type { MedicationsFormModalValuesProps } from 'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal';
import type { RemindersFormSubmitValues } from 'src/sections/@dashboard/profile/components/ProfileRemindersFormModal';
import type { TreatmentFormSubmitValues } from 'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentFormModal';
import type { TreatmentFormSnapshot } from 'src/sections/@dashboard/profile/components/ProfileTreatments/treatmentFormValues';
import {
  UpdateProfileTreatmentDto,
  UpdateReminderDto,
  VisitActivityResponseDto,
  VisitActivityResponseDtoActionEnum,
  VisitActivityResponseDtoTypeEnum,
} from 'woofwoof-api';
import {
  facilityMedicationBody,
  useMedicationEditSeries,
} from 'src/sections/@dashboard/visit/hooks/useMedicationWriteSeries';
import { useTreatmentEditSeries } from 'src/sections/@dashboard/visit/hooks/useTreatmentWriteSeries';
import { useActivityMedicalScope } from 'src/sections/@dashboard/visit/hooks/useActivityMedicalScope';
import {
  findEntryById,
  isEntryKnownMissing,
} from 'src/sections/@dashboard/visit/utils/activityEntryLookup';
import { editFormReady } from 'src/sections/@dashboard/visit/utils/editFormReady';
import { patientOrVisitRoute } from 'src/sections/@dashboard/warehouse/hooks/useClinicalWriteSeries';
import { useStockWriteError } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteError';
import { useStockWriteFeedback } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteFeedback';
import VisitModalEmptyState from './VisitModalEmptyState';
import { translateKey } from 'src/locales/translate';


// Lazy chunks — pre-warmed on mount via useEffect below.
const MedicationFormModal = lazy(
  () =>
    import(
      'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal'
    ),
);
const ProfileTreatmentFormModal = lazy(
  () =>
    import(
      'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentFormModal'
    ),
);
const ProfileRemindersFormModal = lazy(
  () =>
    import(
      'src/sections/@dashboard/profile/components/ProfileRemindersFormModal'
    ),
);

// This tab now only edits / removes existing activity items — the "add to
// visit" flow lives in VisitAddMenu. Modals open exclusively via handleOpenEdit,
// so there's no document modal here (documents aren't editable in-place).
type ActiveModal = 'medication' | 'treatment' | 'reminder' | null;

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  [VisitActivityResponseDtoTypeEnum.Medication]: 'Lek',
  [VisitActivityResponseDtoTypeEnum.Treatment]: translateKey("visit.visitCourseMenuTreatment"),
  [VisitActivityResponseDtoTypeEnum.Reminder]: translateKey("visit.visitCourseMenuReminder"),
  [VisitActivityResponseDtoTypeEnum.Document]: translateKey("visit.visitCourseMenuDocument"),
  TRANSCRIPTION: 'Transkrypcja',
};

const ACTIVITY_ACTION_LABELS: Record<string, string> = {
  [VisitActivityResponseDtoActionEnum.Added]: 'Dodano',
  [VisitActivityResponseDtoActionEnum.Updated]: 'Zaktualizowano',
  [VisitActivityResponseDtoActionEnum.Removed]: translateKey("hardcoded.usunieto"),
  STARTED: 'Rozpoczęto',
  STOPPED: 'Zakończono',
  PAUSED: 'Wstrzymano',
  RESUMED: 'Wznowiono',
};

interface VisitModalActivityTabProps {
  items: VisitActivityResponseDto[];
  canEdit: boolean;
  visitId: string;
  organizationId?: string;
  patientId?: string;
  singleReminderOnly?: boolean;
  onActivityChange: () => void;
}

export default function VisitModalActivityTab({
  items,
  canEdit,
  visitId,
  organizationId,
  patientId,
  singleReminderOnly = false,
  onActivityChange,
}: VisitModalActivityTabProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const writeError = useStockWriteError();
  const onStockWrite = useStockWriteFeedback();
  const queryClient = useQueryClient();
  const isGuestVisit = !patientId;
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [editingReferenceId, setEditingReferenceId] = useState<string | null>(
    null,
  );

  // Pre-warm lazy modal chunks so the first edit-modal open is instant.
  useEffect(() => {
    const preload = () => {
      import(
        'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal'
      );
      import(
        'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentFormModal'
      );
      import(
        'src/sections/@dashboard/profile/components/ProfileRemindersFormModal'
      );
    };
    const ric = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void) => number;
        cancelIdleCallback?: (handle: number) => void;
      }
    ).requestIdleCallback;
    if (typeof ric === 'function') {
      const handle = ric(preload);
      return () => {
        (
          window as unknown as { cancelIdleCallback?: (handle: number) => void }
        ).cancelIdleCallback?.(handle);
      };
    }
    const timer = setTimeout(preload, 500);
    return () => clearTimeout(timer);
  }, []);

  // Entity data for edit mode. A medication or treatment activity entry can
  // name a record created during a *different* visit of the same patient
  // (e.g. an earlier visit started it, this one adjusted the dose), so the
  // lookup follows VisitCourseCard's split: the patient's own lists when the
  // visit has a registered patient, the visit-scoped lists on a guest visit,
  // which has no profile to query. Reminders stay visit-scoped either way —
  // the visit-scoped reminder endpoint already works identically for guest
  // and authed visits.
  const { medications, treatments } = useActivityMedicalScope(
    visitId,
    patientId,
  );
  const { data: remindersData } = useQuery(
    ['visit-reminders-modal', visitId],
    () => visitScopedPrescribingApi.listReminders(visitId),
    { enabled: Boolean(visitId) },
  );
  const reminders = remindersData?.data;

  // Treatment catalog for the "Edytuj zabieg" form — shared across both guest
  // and authed paths because the catalog belongs to the organization, not the
  // patient profile.
  const { data: catalogData } = useQuery(
    ['treatment-catalog-modal-activity', organizationId],
    () =>
      treatmentApi.treatmentControllerGetCatalog({
        organizationId: organizationId!,
      }),
    { enabled: canEdit && Boolean(organizationId) },
  );

  const editingMedication = editingReferenceId
    ? findEntryById(medications, editingReferenceId)
    : undefined;

  const editingTreatment = editingReferenceId
    ? findEntryById(treatments, editingReferenceId)
    : undefined;

  const editingReminder = editingReferenceId
    ? findEntryById(reminders, editingReferenceId)
    : undefined;

  // M-4: an edit form opens only once its entry is loaded.
  const formReady = editFormReady(
    editingReferenceId,
    activeModal === 'medication'
      ? editingMedication
      : activeModal === 'treatment'
      ? editingTreatment
      : editingReminder,
  );

  const handleCloseModal = () => {
    setActiveModal(null);
    setEditingReferenceId(null);
  };

  // The pencil may be clicked before its list has loaded, opening the modal
  // optimistically (formReady above gates the form itself). Once the list in
  // scope for the active modal has loaded and genuinely does not hold the
  // entry — a stale reference, or one whose visit was mislabelled — reset
  // instead of leaving the modal state set with nothing to show for it.
  useEffect(() => {
    if (!activeModal || !editingReferenceId) return;
    const listInScope =
      activeModal === 'medication'
        ? medications
        : activeModal === 'treatment'
        ? treatments
        : reminders;
    if (isEntryKnownMissing(listInScope, editingReferenceId)) {
      handleCloseModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, editingReferenceId, medications, treatments, reminders]);

  const handleOpenEdit = (type: string, referenceId: string) => {
    setEditingReferenceId(referenceId);
    const modalType =
      type === VisitActivityResponseDtoTypeEnum.Medication
        ? 'medication'
        : type === VisitActivityResponseDtoTypeEnum.Treatment
        ? 'treatment'
        : type === VisitActivityResponseDtoTypeEnum.Reminder
        ? 'reminder'
        : null;
    setActiveModal(modalType);
  };

  // --- Mutations ---
  const { mutate: removeActivity } = useMutation({
    mutationFn: (activityId: string) =>
      organizationProfileApi.organizationProfileControllerRemoveVisitActivity({
        visitId,
        activityId,
      }),
    // A removed medication or treatment reverses its stock.
    onSuccess: (response) => {
      onStockWrite(response);
      onActivityChange();
      enqueueSnackbar(t('visit.visitCourseRemoveSuccess'), {
        variant: 'success',
      });
    },
    // M-1: a refused removal says why (e.g. the drug came from a treatment).
    onError: writeError(t('visit.visitCourseRemoveError')),
  });

  // R1-2: an entry here may come from an earlier visit of the patient (see
  // useActivityMedicalScope), and the visit's own endpoints refuse an entry
  // of another visit. So a registered patient's edit goes through the
  // patient's endpoints with this visit as the context (the activity entry,
  // the reminder), as VisitCourseCard's does; a guest visit keeps the
  // visit's endpoints. R3-4: an edit's series is its entry's.
  const writeRoute = patientOrVisitRoute(patientId, visitId);
  const medicationSeries = useMedicationEditSeries({
    route: writeRoute,
    editingId: editingReferenceId ?? '',
    onSuccess: () => {
      onActivityChange();
      // The edit form's data came from `patient-medications` (registered
      // patient); refresh it so a reopened edit shows the saved values.
      if (!isGuestVisit) {
        queryClient.invalidateQueries(['patient-medications', patientId]);
      }
      enqueueSnackbar(t('visit.visitCourseAddSuccess'), { variant: 'success' });
      handleCloseModal();
    },
    onError: writeError(translateKey('snackbar.medicationUpdateError')),
  });

  const treatmentSeries = useTreatmentEditSeries({
    route: writeRoute,
    editingId: editingReferenceId ?? '',
    onSuccess: () => {
      onActivityChange();
      // Same as above, for `profile-treatments`.
      if (!isGuestVisit) {
        queryClient.invalidateQueries(['profile-treatments', patientId]);
      }
      enqueueSnackbar(t('visit.visitCourseAddSuccess'), { variant: 'success' });
      handleCloseModal();
    },
    onError: writeError(translateKey('snackbar.treatmentUpdateError')),
  });

  const { mutate: updateReminder, isLoading: isUpdatingReminder } = useMutation(
    {
      mutationFn: (payload: UpdateReminderDto) =>
        visitScopedPrescribingApi.updateReminder(
          visitId,
          editingReferenceId || '',
          payload,
        ),
      onSuccess: () => {
        onActivityChange();
        enqueueSnackbar(t('visit.visitCourseAddSuccess'), {
          variant: 'success',
        });
        handleCloseModal();
      },
    },
  );

  // --- Submit handlers (edit-only; modals open exclusively via handleOpenEdit,
  // so editingReferenceId is always set when these fire) ---
  const handleMedicationSubmit = (data: MedicationsFormModalValuesProps) => {
    medicationSeries.submit(
      editingReferenceId ?? '',
      facilityMedicationBody(data),
      data,
    );
  };

  const handleTreatmentSubmit = (
    values: TreatmentFormSubmitValues,
    shown: TreatmentFormSnapshot,
  ) => {
    // An edit names no clinic: `UpdateProfileTreatmentDto` has no such field.
    treatmentSeries.submit(
      editingReferenceId ?? '',
      values as UpdateProfileTreatmentDto,
      shown,
    );
  };

  const handleReminderSubmit = (values: RemindersFormSubmitValues) => {
    updateReminder(values as UpdateReminderDto);
  };

  if (items.length === 0 && !canEdit) {
    return <VisitModalEmptyState message={translateKey("visit.visitCourseEmpty")} />;
  }

  return (
    <>
      <Stack sx={{ gap: 0, flex: 1, minHeight: 0 }}>
        {items.length === 0 && (
          <VisitModalEmptyState message={translateKey("visit.visitCourseEmpty")} />
        )}

        {[...items].reverse().map((item, index) => {
          const isLast = index === items.length - 1;
          // Whichever list backs this item's type — a medication or treatment
          // from another visit is still a dead click if its list has loaded
          // and genuinely doesn't hold it.
          const listForType =
            item.type === VisitActivityResponseDtoTypeEnum.Medication
              ? medications
              : item.type === VisitActivityResponseDtoTypeEnum.Treatment
              ? treatments
              : item.type === VisitActivityResponseDtoTypeEnum.Reminder
              ? reminders
              : undefined;
          const canEditItem =
            canEdit &&
            (item.action === VisitActivityResponseDtoActionEnum.Added ||
              item.action === VisitActivityResponseDtoActionEnum.Updated) &&
            !isEntryKnownMissing(listForType, item.referenceId);
          const canRemove =
            canEdit &&
            item.action === VisitActivityResponseDtoActionEnum.Added;
          const isEditableType =
            item.type !== VisitActivityResponseDtoTypeEnum.Document;
          // On/off events (e.g. transcription) have no named item to append.
          const showName =
            item.action === VisitActivityResponseDtoActionEnum.Added ||
            item.action === VisitActivityResponseDtoActionEnum.Updated ||
            item.action === VisitActivityResponseDtoActionEnum.Removed;

          return (
            <Stack
              key={item.id}
              sx={{
                flexDirection: 'row',
                alignItems: 'stretch',
                borderRadius: 1,
                transition: 'background-color 0.15s',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Stack
                sx={{
                  alignItems: 'center',
                  width: 28,
                  flexShrink: 0,
                  position: 'relative',
                  justifyContent: 'center',
                }}
              >
                {index > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      width: 2,
                      top: 0,
                      bottom: '50%',
                      backgroundColor: theme.palette.divider,
                    }}
                  />
                )}
                {!isLast && (
                  <Box
                    sx={{
                      position: 'absolute',
                      width: 2,
                      top: '50%',
                      bottom: 0,
                      backgroundColor: theme.palette.divider,
                    }}
                  />
                )}
                <VetsiIcon
                  width={16}
                  height={16}
                  sx={{ flexShrink: 0, zIndex: 1 }}
                />
              </Stack>
              <Stack
                sx={{
                  flex: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                  ml: 1,
                  minWidth: 0,
                }}
              >
                <Stack
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    gap: 0.25,
                  }}
                >
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {ACTIVITY_TYPE_LABELS[item.type] || item.type}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {format(new Date(item.createdAt), 'HH:mm')}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                  >
                    {ACTIVITY_ACTION_LABELS[item.action] || item.action}
                    {showName ? `: ${item.name}` : ''}
                  </Typography>
                </Stack>
                {canEditItem && isEditableType && (
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEdit(item.type, item.referenceId)}
                    sx={{
                      color: theme.palette.text.disabled,
                      flexShrink: 0,
                      '&:hover': { color: theme.palette.primary.main },
                    }}
                  >
                    <Iconify icon="eva:edit-2-outline" width={18} />
                  </IconButton>
                )}
                {canRemove && (
                  <IconButton
                    size="small"
                    onClick={() => removeActivity(item.id)}
                    sx={{
                      color: theme.palette.text.disabled,
                      flexShrink: 0,
                      '&:hover': { color: theme.palette.error.main },
                    }}
                  >
                    <Iconify icon="eva:trash-2-outline" width={18} />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      <Suspense fallback={null}>
        {activeModal === 'medication' && formReady && (
          <MedicationFormModal
            isModalOpen={true}
            closeModal={handleCloseModal}
            medication={editingMedication}
            linkedReminder={editingMedication?.linkedReminder}
            onSubmit={handleMedicationSubmit}
            onDelete={
              editingReferenceId
                ? () =>
                    removeActivity(
                      items.find(
                        (i) =>
                          i.referenceId === editingReferenceId &&
                          i.type ===
                            VisitActivityResponseDtoTypeEnum.Medication &&
                          i.action === VisitActivityResponseDtoActionEnum.Added,
                      )?.id || '',
                    )
                : undefined
            }
            isCreating={false}
            isUpdating={medicationSeries.isLoading}
            singleReminderOnly={singleReminderOnly}
            stockEnabled
            unconfirmedSave={medicationSeries.pending}
            unconfirmedBlocked={medicationSeries.blocked}
            unconfirmedValues={medicationSeries.frozenValues}
            stockWriteError={medicationSeries.lastError}
            ownerProfileId={patientId || undefined}
          />
        )}
        {activeModal === 'treatment' && formReady && (
          <ProfileTreatmentFormModal
            isModalOpen={true}
            closeModal={handleCloseModal}
            treatment={editingTreatment}
            onSubmit={handleTreatmentSubmit}
            stockEnabled
            unconfirmedSave={treatmentSeries.pending}
            unconfirmedBlocked={treatmentSeries.blocked}
            unconfirmedValues={treatmentSeries.frozenValues}
            onDelete={
              editingReferenceId
                ? () =>
                    removeActivity(
                      items.find(
                        (i) =>
                          i.referenceId === editingReferenceId &&
                          i.type ===
                            VisitActivityResponseDtoTypeEnum.Treatment &&
                          i.action === VisitActivityResponseDtoActionEnum.Added,
                      )?.id || '',
                    )
                : () => {}
            }
            isProfessional
            organizationId={organizationId}
            catalogOptions={catalogData?.data ?? []}
            isCreating={false}
            isUpdating={treatmentSeries.isLoading}
            isDeleting={false}
            singleReminderOnly={singleReminderOnly}
          />
        )}
        {activeModal === 'reminder' && formReady && (
          <ProfileRemindersFormModal
            isModalOpen={true}
            closeModal={handleCloseModal}
            reminder={editingReminder}
            onSubmit={handleReminderSubmit}
            onDelete={
              editingReferenceId
                ? () =>
                    removeActivity(
                      items.find(
                        (i) =>
                          i.referenceId === editingReferenceId &&
                          i.type === VisitActivityResponseDtoTypeEnum.Reminder &&
                          i.action === VisitActivityResponseDtoActionEnum.Added,
                      )?.id || '',
                    )
                : () => {}
            }
            isCreating={false}
            isUpdating={isUpdatingReminder}
            isDeleting={false}
            singleReminderOnly={singleReminderOnly}
          />
        )}
      </Suspense>
    </>
  );
}
